import * as React from "react"
import {
  AlertTriangle,
  Activity,
  BookOpen,
  Briefcase,
  ChevronDown,
  ChevronRight,
  Compass,
  Database,
  Home,
  Library,
  Plane,
  Plus,
  Search,
  Settings,
} from "lucide-react"
import { Link, NavLink, useParams, matchPath, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"

import { APP_NAME_TITLE } from "@/lib/app-brand"
import { LogoIcon } from "@/components/logo"
import { SidebarProfileDropdown } from "@/components/shell/sidebar-profile-dropdown"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  Sidebar as SidebarRoot,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar"
import { useUserProfile } from "@/hooks/use-user-profile"
import { useWorkspaces } from "@/hooks/use-workspaces"
import { icon } from "@/lib/design-tokens"
import { FREE_TIER, isPaidPlan } from "@/lib/usage-limits"
import { getSanityStudioUrl } from "@/lib/sanity"
import { supabase } from "@/lib/supabase"
import { cn } from "@/lib/utils"
import { useShell } from "@/providers/shell-context"

const sidebarSeparatorBetweenNavClassName = "my-1"

type HealthStatus = "ok" | "warning" | "error"

function statusRank(s: HealthStatus): number {
  if (s === "error") return 2
  if (s === "warning") return 1
  return 0
}

function worstHealthStatus(statuses: HealthStatus[]): HealthStatus {
  let worst: HealthStatus = "ok"
  for (const s of statuses) {
    if (statusRank(s) > statusRank(worst)) worst = s
  }
  return worst
}

function healthDotClass(s: HealthStatus): string {
  if (s === "ok") return "bg-emerald-500"
  if (s === "warning") return "bg-amber-500"
  return "bg-destructive"
}

function SidebarCommandPaletteTrigger() {
  const { t } = useTranslation()
  const { setCommandPaletteOpen } = useShell()

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <SidebarMenuButton
          type="button"
          onClick={() => setCommandPaletteOpen(true)}
          tooltip={t("commandPalette.openShortcutHint")}
          aria-label={t("commandPalette.openShortcutHint")}
          className="border border-sidebar-border/60 bg-sidebar-accent/30 font-mono text-xs font-medium tracking-tight text-sidebar-foreground/70 shadow-none hover:bg-sidebar-accent hover:text-sidebar-foreground"
        >
          <Search className="shrink-0 opacity-70" aria-hidden />
          <span className="ml-auto shrink-0 group-data-[collapsible=icon]:sr-only">⌘K</span>
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}

/** Admin sidebar: only tooling routes; dashboard/workspaces live in the main app sidebar. */
function AdminSidebarMenus({
  isCollapsed,
  pathname,
}: {
  isCollapsed: boolean
  pathname: string
}) {
  const { t } = useTranslation()
  const [healthStatus, setHealthStatus] = React.useState<HealthStatus>("ok")

  React.useEffect(() => {
    let mounted = true
    async function run() {
      const { data, error } = await supabase.from("system_health").select("status")
      if (!mounted) return
      if (error) {
        setHealthStatus("warning")
        return
      }
      const statuses = (data ?? [])
        .map((r) => (r as { status?: unknown }).status)
        .filter((s): s is HealthStatus => s === "ok" || s === "warning" || s === "error")
      setHealthStatus(worstHealthStatus(statuses))
    }
    void run()
    const id = window.setInterval(() => void run(), 60_000)
    return () => {
      mounted = false
      window.clearInterval(id)
    }
  }, [])

  const [reviewQueueCount, setReviewQueueCount] = React.useState(0)
  React.useEffect(() => {
    let mounted = true
    async function run() {
      const { count, error } = await supabase
        .from("visa_metadata")
        .select("id", { count: "exact", head: true })
        .or("verification_status.eq.pending_review,flag_for_review.eq.true")
      if (!mounted) return
      if (error) {
        setReviewQueueCount(0)
        return
      }
      setReviewQueueCount(typeof count === "number" ? count : 0)
    }
    void run()
    const id = window.setInterval(() => void run(), 60_000)
    return () => {
      mounted = false
      window.clearInterval(id)
    }
  }, [])

  const isAdminOverview = matchPath({ path: "/admin/health", end: true }, pathname) != null
  const isAdminSettings = matchPath({ path: "/admin/settings", end: true }, pathname) != null
  const isAdminVisaMetadata = pathname.startsWith("/admin/visa-metadata")
  const isAdminAirlines = matchPath({ path: "/admin/airlines", end: true }, pathname) != null
  const isAdminResources = matchPath({ path: "/admin/resources", end: true }, pathname) != null

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton asChild isActive={isAdminOverview} tooltip={t("admin.health.title")}>
            <NavLink to="/admin/health">
              <Activity className={cn(icon.md, "shrink-0 opacity-80")} aria-hidden />
              <span className="flex min-w-0 items-center gap-2">
                <span className="truncate">{t("admin.overview", { defaultValue: "Overview" })}</span>
                <span
                  className={cn(
                    "inline-block size-2 shrink-0 rounded-full",
                    healthDotClass(healthStatus),
                    isCollapsed ? "group-data-[collapsible=icon]:hidden" : ""
                  )}
                  aria-hidden
                />
              </span>
            </NavLink>
          </SidebarMenuButton>
        </SidebarMenuItem>
        <SidebarMenuItem>
          <SidebarMenuButton asChild isActive={isAdminSettings} tooltip={t("admin.settings.nav")}>
            <NavLink to="/admin/settings">
              <Settings className={cn(icon.md, "shrink-0 opacity-80")} aria-hidden />
              <span>{t("admin.settings.nav")}</span>
            </NavLink>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
      <SidebarSeparator className={sidebarSeparatorBetweenNavClassName} />
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton asChild isActive={isAdminVisaMetadata} tooltip={t("admin.visaMetadata.nav")}>
            <NavLink to="/admin/visa-metadata">
              <Database className={cn(icon.md, "shrink-0 opacity-80")} aria-hidden />
              <span>{t("admin.visaMetadata.nav")}</span>
              {reviewQueueCount > 0 ? (
                <SidebarMenuBadge className="bg-primary/15 text-primary peer-data-active/menu-button:text-primary">
                  {reviewQueueCount > 99 ? "99+" : reviewQueueCount}
                </SidebarMenuBadge>
              ) : null}
            </NavLink>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
      <SidebarSeparator className={sidebarSeparatorBetweenNavClassName} />
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton asChild isActive={isAdminAirlines} tooltip={t("admin.airlines")}>
            <NavLink to="/admin/airlines">
              <Plane className={cn(icon.md, "shrink-0 opacity-80")} aria-hidden />
              <span>{t("admin.airlines")}</span>
            </NavLink>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
      <SidebarSeparator className={sidebarSeparatorBetweenNavClassName} />
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton
            asChild
            isActive={isAdminResources}
            tooltip={t("admin.resources.nav")}
          >
            <NavLink to="/admin/resources">
              <Library className={cn(icon.md, "shrink-0 opacity-80")} aria-hidden />
              <span>{t("admin.resources.nav")}</span>
            </NavLink>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
      <SidebarSeparator className={sidebarSeparatorBetweenNavClassName} />
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton asChild isActive={false} tooltip={t("admin.blogDescription")}>
            <a href={getSanityStudioUrl()} target="_blank" rel="noopener noreferrer">
              <BookOpen className={cn(icon.md, "shrink-0 opacity-80")} aria-hidden />
              <span>{t("admin.blog")}</span>
            </a>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    </>
  )
}

export function Sidebar() {
  const { t } = useTranslation()
  const { workspaceId: activeWorkspaceId } = useParams<{ workspaceId: string }>()
  const { workspaces, loading } = useWorkspaces()
  const { profile } = useUserProfile()
  const { pathname } = useLocation()
  const isGuidedNewWorkspace = matchPath({ path: "/dashboard/workspaces/new", end: true }, pathname) != null
  const { state } = useSidebar()
  const isCollapsed = state === "collapsed"

  const inAdminZone = pathname.startsWith("/admin")
  /** Only treat as admin UX when backend says admin — avoids flashing admin sidebar before redirect */
  const showAdminSidebar = inAdminZone && profile?.is_admin === true

  const isDashboardHome = matchPath({ path: "/dashboard", end: true }, pathname) != null
  const isVisaExplorerHub = matchPath({ path: "/dashboard/visa-explorer", end: true }, pathname) != null

  const [workspacesOpen, setWorkspacesOpen] = React.useState(true)
  const ownedCount = React.useMemo(
    () => workspaces.filter((w) => w.accessRole === "owner").length,
    [workspaces]
  )
  const isFree = !isPaidPlan(profile?.plan)
  const atWorkspaceLimit = isFree && ownedCount >= FREE_TIER.maxOwnedWorkspaces

  return (
    <SidebarRoot variant="floating" collapsible="icon">
      <SidebarHeader
        className={cn(
          "flex gap-2 md:pt-1",
          isCollapsed
            ? "flex-col items-center md:pt-3.5"
            : "flex-row items-center justify-between"
        )}
      >
        <Link
          to="/dashboard"
          className={cn(
            "flex min-w-0 items-center gap-2 rounded-md font-sans text-sm font-semibold tracking-tight text-sidebar-foreground transition-colors hover:bg-sidebar-muted hover:text-foreground",
            isCollapsed ? "justify-center p-1.5" : "px-2 py-1.5"
          )}
        >
          <LogoIcon className="size-7 shrink-0 text-primary" />
          {!isCollapsed ? (
            <span className="flex min-w-0 flex-col truncate leading-tight">
              <span className="truncate">{APP_NAME_TITLE}</span>
              {showAdminSidebar ? (
                <span className="truncate text-xs font-medium normal-case tracking-normal text-sidebar-foreground/50">
                  {t("admin.shellLabel")}
                </span>
              ) : null}
            </span>
          ) : null}
        </Link>
        <div className="flex shrink-0 items-center gap-1">
          <SidebarTrigger
            className={cn("shrink-0", isCollapsed ? "mx-auto md:mx-0" : undefined)}
          />
        </div>
      </SidebarHeader>

      <SidebarContent className="gap-1 px-2 py-2">
        {showAdminSidebar ? (
          <>
            <SidebarCommandPaletteTrigger />
            <SidebarSeparator className={sidebarSeparatorBetweenNavClassName} />
            <AdminSidebarMenus isCollapsed={isCollapsed} pathname={pathname} />
          </>
        ) : (
          <>
            <SidebarCommandPaletteTrigger />
            <SidebarSeparator className={sidebarSeparatorBetweenNavClassName} />

            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={isDashboardHome} tooltip={t("nav.dashboard")}>
                  <NavLink to="/dashboard" end>
                    <Home className={cn(icon.md, "shrink-0 opacity-80")} aria-hidden />
                    <span>{t("nav.dashboard")}</span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>

            <SidebarSeparator className={sidebarSeparatorBetweenNavClassName} />

            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={isVisaExplorerHub} tooltip={t("nav.visaExplorer")}>
                  <NavLink to="/dashboard/visa-explorer">
                    <Compass className={cn(icon.md, "shrink-0 opacity-80")} aria-hidden />
                    <span>{t("nav.visaExplorer")}</span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>

            <SidebarSeparator className={sidebarSeparatorBetweenNavClassName} />

            <SidebarMenu>
              <Collapsible
                open={workspacesOpen}
                onOpenChange={setWorkspacesOpen}
                className="group/collapsible"
              >
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton
                      tooltip={t("nav.workspaces")}
                      className="pr-2"
                    >
                      <Briefcase className={cn(icon.md, "shrink-0 opacity-80")} aria-hidden />
                      <span className="flex min-w-0 flex-1 items-center justify-between gap-2 truncate">
                        <span className="truncate">{t("nav.workspaces")}</span>
                        {isFree && !loading ? (
                          <span
                            className={cn(
                              "inline-flex shrink-0 items-center gap-0.5 tabular-nums text-xs font-medium group-data-[collapsible=icon]:hidden",
                              atWorkspaceLimit
                                ? "text-destructive"
                                : "text-sidebar-foreground/55"
                            )}
                            title={t("usage.sidebarWorkspaces", {
                              used: ownedCount,
                              max: FREE_TIER.maxOwnedWorkspaces,
                            })}
                          >
                            {atWorkspaceLimit ? (
                              <AlertTriangle className={cn(icon.xs, "shrink-0")} aria-hidden />
                            ) : null}
                            {ownedCount}/{FREE_TIER.maxOwnedWorkspaces}
                          </span>
                        ) : null}
                      </span>
                      {workspacesOpen ? (
                        <ChevronDown className={cn(icon.md, "ml-auto shrink-0 opacity-60 group-data-[collapsible=icon]:hidden")} />
                      ) : (
                        <ChevronRight className={cn(icon.md, "ml-auto shrink-0 opacity-60 group-data-[collapsible=icon]:hidden")} />
                      )}
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub className="border-sidebar-border">
                      {loading ? (
                        <li className="px-2 py-1.5 text-xs text-sidebar-foreground/50">
                          {t("nav.loading")}
                        </li>
                      ) : workspaces.length === 0 ? (
                        <li className="px-2 py-2 text-xs text-sidebar-foreground/50">
                          {t("emptyStates.dashboard.title")}
                        </li>
                      ) : (
                        workspaces.map((ws) => {
                          const isWsActive = activeWorkspaceId === ws.id
                          return (
                            <SidebarMenuSubItem key={ws.id}>
                              <SidebarMenuSubButton asChild isActive={isWsActive} size="md">
                                <Link to={`/workspaces/${ws.id}/overview`}>
                                  <span className="truncate">{ws.name}</span>
                                </Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          )
                        })
                      )}
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton asChild isActive={isGuidedNewWorkspace} size="md">
                          <Link to="/dashboard/workspaces/new">
                            <Plus className={cn(icon.md, "shrink-0 opacity-80")} aria-hidden />
                            <span className="truncate">{t("dashboard.createWorkspace")}</span>
                          </Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
            </SidebarMenu>
          </>
        )}
      </SidebarContent>

      <SidebarFooter className="mt-auto gap-2 border-t border-border pt-3">
        <SidebarProfileDropdown />
      </SidebarFooter>
    </SidebarRoot>
  )
}
